import { Box, Grid, List, Stack, styled } from '@mui/material';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { WsButton, WsDrawer, WsInputNumber } from 'wisestep-base';

import CheckboxFilter from '../../../../components/filters/components/CheckboxFilter';
import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import MultiMatchFilter from '../../../../components/filters/components/MultiMatchFilter';
import RadioListFilter from '../../../../components/filters/components/RadioListFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import ExperienceRangeFilter from '../../components/ExperienceRangeFilter';
import BasicSearchForm from '../../types/IBasicSearchForm';
import CareerBuilderAdvanceSearch from '../types/ICareerBuilderAdvanceSearch';

interface AdvanceFilterProps {
  open: boolean;
  onClose: () => void;
  performSearch: (values: BasicSearchForm & CareerBuilderAdvanceSearch) => void;
}

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const AdvanceFilter = ({ open, onClose, performSearch }: AdvanceFilterProps) => {
  const methods = useFormContext();
  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => onClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <Box pb={7}>
            <List>
              <RadioListFilter
                name="searchPattern"
                row={false}
                options={[
                  { label: 'Match All Keywords', value: 'All' },
                  { label: 'Match Any Keywords', value: 'Any' },
                  { label: 'Match Exact Keywords', value: 'Exact' },
                  { label: 'Match keyword using Boolean Logic', value: 'Boo' },
                ]}
                title="Search Pattern"
              />
              <ExperienceRangeFilter />
              <MultiMatchFilter
                name="jobTitle.values"
                title="Job Title"
                placeholder="Job Titles"
                matchTypeFieldName="jobTitle.matchType"
              />
              <MultiMatchFilter
                name="companies.values"
                title="Companies"
                placeholder="Companies"
                matchTypeFieldName="companies.matchType"
              />

              <CheckboxListFilter
                name="minimumDegree"
                options={{
                  CE31: 'High School',
                  CE32: '2 Years Degree',
                  CE321: '4 Years Degree',
                  CE3210: 'High School',
                }}
                title="Minimum Degree"
              />

              <CheckboxListFilter
                name="languages"
                options={{
                  LAEN: 'English',
                  LAES: 'Spanish',
                  LAFR: 'French',
                  LADE: 'German',
                  LARU: 'Russian',
                  LAIT: 'Italian',
                  LAHI: 'Hindi',
                  LAPT: 'Portuguese',
                  LAJA: 'Japanese',
                  LAAR: 'Arabic',
                }}
                title="Languages"
              />
              <CheckboxFilter name="applyLastActivity" title="Last Active" label="Last Active" />

              <CheckboxListFilter
                name="employmentType"
                options={{
                  All: 'All',
                  ETFE: 'Full-Time',
                  ETPE: 'Part-Time',
                  ETCT: 'Contractor',
                  ETIN: 'Intern',
                }}
                title="Employment Type"
              />

              <CheckboxListFilter
                name="authorizedCountries"
                options={{
                  US: 'United States',
                  UK: 'United Kingdom',
                  CA: 'Canada',
                  IE: 'Ireland',
                  IN: 'India',
                  IT: 'Italy',
                  ES: 'Spain',
                  PL: 'Poland',
                  BR: 'Brazil',
                  LT: 'Lithuania',
                  PT: 'Portugal',
                  NG: 'Nigeria',
                  AU: 'Australia',
                  MX: 'Mexico',
                  HU: 'Hungary',
                }}
                title="Countries"
              />

              <CheckboxListFilter
                name="workAuthorization"
                options={{
                  CTCT: 'US Citizenship',
                  CTGR: 'Green Card',
                  CTEM: 'Have H1',
                  EAEA: 'Employment Auth Document',
                  CTNO: 'Need H1',
                  EATN: 'Tn Permit Holder',
                  CTNS: 'Not Specified ',
                }}
                title="Work Authorization"
              />

              <SalaryRangeFilter
                title="Compensation"
                selectArray={[]}
                selectArray1={[
                  { label: 'Salary', value: 'SALR' },
                  { label: 'Hourly', value: 'HOUR' },
                ]}
              />

              <RadioListFilter
                name="hasSecurityClearance"
                row={false}
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                  { label: 'Either', value: '' },
                ]}
                title="Has Security Clearance"
              />

              <RadioListFilter
                name="currentlyEmployed"
                row={false}
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                  { label: 'Either', value: '' },
                ]}
                title="Currently Employed"
              />

              <RadioListFilter
                name="managementExperience"
                row={false}
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                  { label: 'Either', value: '' },
                ]}
                title="Management Experience"
              />

              <WsAccordion title={'Employees Managed'}>
                <Box>
                  <WsInputNumber name="minEmployeeManaged" label="Min" />
                </Box>
              </WsAccordion>

              <CheckboxListFilter
                name="militaryExperience "
                options={{
                  M1: 'Active Duty',
                  M2: 'Reservist-Drilling',
                  M3: 'National Guard-Drilling',
                  M4: 'Inactive Reserve',
                  M5: 'Inactive National Guard',
                  M6: 'Retired Military',
                  M7: 'Veteran',
                }}
                title="Military Experience "
              />

              <RadioListFilter
                name="travelPreference"
                row={false}
                options={[
                  { label: 'Not Specified', value: '' },
                  { label: 'None', value: 'RTNONE' },
                  { label: 'Negligible', value: 'RT3210' },
                  { label: 'Up To 25%', value: 'RT321' },
                  { label: 'Up To 50%', value: 'RT32' },
                  { label: 'Road Warrior', value: 'RT3' },
                ]}
                title="Travel Preference"
              />
            </List>
          </Box>
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AdvanceFilter;
